"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { getApiBaseURL } from "@/lib/api/custom-client";
import { createPublicGeminiChatSession } from "@/lib/api/generated/public-gemini-chatbot/public-gemini-chatbot";

/** 回答に出てきた登録商品。吹き出しの下にリンクとして出す */
export type GeminiProduct = {
  id: string;
  name: string;
  url: string;
};

export type GeminiConversation = {
  role: "user" | "assistant";
  text: string;
  timestamp: string;
  /** 読み上げを聞き直すためのURL。直近のターンぶんだけ保持する */
  audioUrl?: string;
  products?: GeminiProduct[];
};

export type GeminiStatus =
  | "disconnected"
  | "connecting"
  | "ready"
  | "error";

/** 直近ターンの実測値。VADのチューニング判断に使う */
export type GeminiTurnMetrics = {
  /** 発話終了をサーバが検出してから最初のテキストが届くまで（クライアント計測） */
  responseStartMs: number;
  /** Gemini の TTFT（サーバ計測） */
  ttftMs: number;
  totalMs: number;
  promptTokens: number;
  outputTokens: number;
  cachedTokens: number;
};

/** 録音は16kHz/モノラル/PCM16。VADもこの前提で組んである */
const TARGET_SAMPLE_RATE = 16000;
const SCRIPT_PROCESSOR_BUFFER_SIZE = 4096;

/**
 * 再生はサーバがターンごとに送るWAVヘッダからフォーマットを読む。
 * 届いたPCMをそのまま1つずつ鳴らすとブツ切りになるため、
 * PLAYBACK_SCHEDULE_MS ぶん溜めてから AudioContext の時間軸に並べる。
 */
const PLAYBACK_SCHEDULE_MS = 150;
/** ターンの終わりに置く間。次の回答と続けて鳴らさない */
const TURN_GAP_SEC = 0.15;
/**
 * 音が途切れたあと鳴らし直すときの先置き。
 * 次のPCMが間に合わないと再生位置が現在時刻に追いつかれるため、少し先に置き直して
 * 続く遅れを吸収する。WebSocketで届くので回線が細いと詰まりやすい。
 */
const PLAYBACK_LEAD_SEC = 0.12;
/** 継ぎ目の波形は不連続になる。数msで立ち上げ・落として、プチッという音を消す */
const FADE_SEC = 0.005;

/** 割り込みで止めるときにフェードさせるため、ゲインと対で持っておく */
type ScheduledAudio = { source: AudioBufferSourceNode; gain: GainNode };

/**
 * 聞き直せるように控えておく読み上げの数。
 *
 * 8秒の回答で約390KBあるので、会話が長くなるほどメモリを食う。
 * 直近ぶんだけ残し、古いものはURLを解放する。
 */
const AUDIO_HISTORY_LIMIT = 10;

/**
 * 回答の表示速度。Geminiは数十文字のチャンクを100ms程度で出し切ってしまうため、
 * 届いた順にそのまま描画すると一瞬で全文が出る。1ティックごとに
 * 「未表示の残り / REVEAL_DIVISOR」文字ずつ出すことで、
 * 生成が速いときは数百ms遅れで追いつき、遅いときは1文字ずつになる。
 */
const REVEAL_TICK_MS = 30;
const REVEAL_DIVISOR = 8;

/** 控えたPCMを、長さの入ったWAVにまとめる */
const encodeWavBlob = (
  chunks: Uint8Array[],
  format: { channels: number; sampleRate: number; bitsPerSample: number },
): Blob => {
  const dataSize = chunks.reduce((total, chunk) => total + chunk.length, 0);
  const header = new DataView(new ArrayBuffer(44));
  const bytesPerSample = format.bitsPerSample / 8;

  const writeAscii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) header.setUint8(offset + i, text.charCodeAt(i));
  };

  writeAscii(0, "RIFF");
  header.setUint32(4, 36 + dataSize, true);
  writeAscii(8, "WAVE");
  writeAscii(12, "fmt ");
  header.setUint32(16, 16, true);
  header.setUint16(20, 1, true); // PCM
  header.setUint16(22, format.channels, true);
  header.setUint32(24, format.sampleRate, true);
  header.setUint32(28, format.sampleRate * format.channels * bytesPerSample, true);
  header.setUint16(32, format.channels * bytesPerSample, true);
  header.setUint16(34, format.bitsPerSample, true);
  writeAscii(36, "data");
  header.setUint32(40, dataSize, true);

  return new Blob([header.buffer, ...chunks.map((chunk) => chunk.slice().buffer)], {
    type: "audio/wav",
  });
};

const useGeminiWs = (agentId: string) => {
  const [status, setStatus] = useState<GeminiStatus>("disconnected");
  const [isRecording, setIsRecording] = useState(false);
  const [conversation, setConversation] = useState<GeminiConversation[]>([]);
  const [currentTranscript, setCurrentTranscript] = useState("");
  /** サーバのVADが発話を検知してから、聞き取り結果が届くまでの間 true */
  const [isSpeechPending, setIsSpeechPending] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState("");
  /** 合成音声を再生している間 true */
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [warning, setWarning] = useState("");
  const [metrics, setMetrics] = useState<GeminiTurnMetrics | null>(null);

  /** 再生し終わった読み上げを聞き直せるように、ターンぶんのPCMを控える */
  const turnPcmRef = useRef<Uint8Array[]>([]);
  /**
   * 音声と商品リンクは回答テキストより後に届く。
   * 表示が追いついていないと吹き出しがまだ無いので、その場合は預かって
   * 吹き出しを作るときに一緒に付ける（前のターンの回答に付けないため）。
   */
  const turnExtrasRef = useRef<{ audioUrl?: string; products?: GeminiProduct[] }>({});
  const answerAppendedRef = useRef(false);
  /** 解放し忘れると Blob がページを閉じるまで残る */
  const audioUrlsRef = useRef<string[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const pendingAutoRecordRef = useRef(false);

  /** 再生側は録音用とサンプリングレートが違うため、AudioContextを分ける */
  const playContextRef = useRef<AudioContext | null>(null);
  const audioFormatRef = useRef<{
    channels: number;
    sampleRate: number;
    bitsPerSample: number;
  } | null>(null);
  const nextPlayTimeRef = useRef(0);
  const pcmQueueRef = useRef<Uint8Array[]>([]);
  const pcmQueueLengthRef = useRef(0);
  /** 割り込みで止められるよう、予約済みの音を持っておく */
  const scheduledSourcesRef = useRef<ScheduledAudio[]>([]);
  /** このターンで1つでも鳴らしたか。ターン頭の置き直しを「途切れ」と数えないため */
  const playbackStartedRef = useRef(false);
  /** 音が途切れた回数。回線の遅れを推測ではなく実測するために持つ */
  const underrunCountRef = useRef(0);
  /** サーバがまだ音声を送ってくる途中かどうか。文の切れ目で表示がちらつくのを防ぐ */
  const audioPendingRef = useRef(false);

  const accumulatedAnswerRef = useRef("");
  /** accumulatedAnswerRef のうち、すでに画面に出した文字数 */
  const revealedLengthRef = useRef(0);
  const revealTimerRef = useRef<number | null>(null);
  /** response_done は受け取ったが、まだ表示が追いついていない状態 */
  const pendingDoneRef = useRef(false);
  /** 直近の文字起こしが届いた時刻。回答が出るまでの体感レイテンシを測るための基準 */
  const turnStartedAtRef = useRef<number | null>(null);
  const responseStartMsRef = useRef(0);

  // ---------------------------------------------------------------------------
  // 録音（PCM16 16kHz をWebSocketにそのまま流す）
  // ---------------------------------------------------------------------------

  const activateRecording = useCallback(() => {
    const stream = mediaStreamRef.current;
    const ws = wsRef.current;
    if (!stream || !ws || ws.readyState !== WebSocket.OPEN) return;

    const audioCtx = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
    audioContextRef.current = audioCtx;

    const source = audioCtx.createMediaStreamSource(stream);
    const processor = audioCtx.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER_SIZE, 1, 1);
    processorRef.current = processor;

    const ratio = audioCtx.sampleRate / TARGET_SAMPLE_RATE;

    processor.onaudioprocess = (event: AudioProcessingEvent) => {
      const input = event.inputBuffer.getChannelData(0);
      const outputLength = Math.floor(input.length / ratio);
      const pcm16 = new Int16Array(outputLength);
      for (let i = 0; i < outputLength; i++) {
        const index = Math.floor(i * ratio);
        const sample = Math.max(-1, Math.min(1, input[index]));
        pcm16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      }
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(pcm16.buffer);
      }
    };

    source.connect(processor);
    processor.connect(audioCtx.destination);

    ws.send(JSON.stringify({ type: "start_recording" }));
    setIsRecording(true);
    pendingAutoRecordRef.current = false;
  }, []);

  // ---------------------------------------------------------------------------
  // 再生（サーバから発話ごとに届くWAV+PCMをつないで鳴らす）
  // ---------------------------------------------------------------------------

  /** 自動再生の制限があるため、ユーザー操作の流れの中で用意して resume しておく */
  const ensurePlaybackContext = useCallback(() => {
    if (!playContextRef.current) {
      playContextRef.current = new AudioContext();
    }
    if (playContextRef.current.state === "suspended") {
      void playContextRef.current.resume();
    }
    return playContextRef.current;
  }, []);

  /** 溜まったPCMをまとめて、前に予約した音の直後に並べる */
  const scheduleQueuedPcm = useCallback(() => {
    const format = audioFormatRef.current;
    const context = playContextRef.current;
    if (!format || !context || pcmQueueLengthRef.current === 0) return;

    const merged = new Uint8Array(pcmQueueLengthRef.current);
    let offset = 0;
    for (const chunk of pcmQueueRef.current) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    pcmQueueRef.current = [];
    pcmQueueLengthRef.current = 0;

    const bytesPerFrame = (format.bitsPerSample / 8) * format.channels;
    const alignedLength = Math.floor(merged.length / bytesPerFrame) * bytesPerFrame;
    // フレームの途中で切れた分は次のチャンクと繋げる
    if (alignedLength < merged.length) {
      const remainder = merged.slice(alignedLength);
      pcmQueueRef.current.push(remainder);
      pcmQueueLengthRef.current = remainder.length;
    }

    const frameCount = alignedLength / bytesPerFrame;
    if (frameCount === 0) return;

    const view = new DataView(merged.buffer, merged.byteOffset, alignedLength);
    const audioBuffer = context.createBuffer(format.channels, frameCount, format.sampleRate);
    for (let channel = 0; channel < format.channels; channel++) {
      const channelData = audioBuffer.getChannelData(channel);
      for (let frame = 0; frame < frameCount; frame++) {
        channelData[frame] = view.getInt16((frame * format.channels + channel) * 2, true) / 32768;
      }
    }

    const source = context.createBufferSource();
    source.buffer = audioBuffer;
    const gain = context.createGain();
    source.connect(gain);
    gain.connect(context.destination);
    source.onended = () => {
      scheduledSourcesRef.current = scheduledSourcesRef.current.filter(
        (scheduled) => scheduled.source !== source,
      );
      // 次の文の合成待ちで一瞬キューが空になるため、サーバの完了通知と両方を見る
      if (scheduledSourcesRef.current.length === 0 && !audioPendingRef.current) {
        setIsSpeaking(false);
      }
    };

    // 予約済みの音が鳴り終わっていたら、そのぶん音が途切れている。
    // 波形の途中で不連続に繋ぐとプチッと鳴るので、置き直したうえで短く立ち上げる。
    const now = context.currentTime;
    let startAt = nextPlayTimeRef.current;
    const interrupted = startAt < now;
    if (interrupted) {
      if (playbackStartedRef.current) {
        underrunCountRef.current += 1;
        console.warn(
          `[GeminiTTS]: 音声が途切れました (${underrunCountRef.current}回目, ${Math.round((now - startAt) * 1000)}ms)`,
        );
      }
      startAt = now + PLAYBACK_LEAD_SEC;
      gain.gain.setValueAtTime(0, startAt);
      gain.gain.linearRampToValueAtTime(1, startAt + FADE_SEC);
    }

    source.start(startAt);
    nextPlayTimeRef.current = startAt + audioBuffer.duration;
    playbackStartedRef.current = true;
    scheduledSourcesRef.current.push({ source, gain });
  }, []);

  const enqueuePcm = useCallback(
    (pcm: Uint8Array) => {
      pcmQueueRef.current.push(pcm);
      pcmQueueLengthRef.current += pcm.length;

      const format = audioFormatRef.current;
      if (!format) return;

      const bytesPerMs =
        (format.sampleRate * (format.bitsPerSample / 8) * format.channels) / 1000;
      if (pcmQueueLengthRef.current >= bytesPerMs * PLAYBACK_SCHEDULE_MS) {
        scheduleQueuedPcm();
      }
    },
    [scheduleQueuedPcm],
  );

  /** ターンの先頭だけWAVヘッダで、以降は生PCMが届く */
  const collectAudioChunk = useCallback(
    (data: ArrayBuffer) => {
      const bytes = new Uint8Array(data);

      if (!audioFormatRef.current) {
        const isWavHeader =
          bytes.length >= 44 &&
          String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]) === "RIFF";
        // フォーマットが分からないまま鳴らすとノイズになるので捨てる
        if (!isWavHeader) return;

        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        const bitsPerSample = view.getUint16(34, true);
        // 再生側は16bit前提で書いてある。違う形式が来たら鳴らさない
        if (bitsPerSample !== 16) {
          console.error("Unsupported TTS audio format:", bitsPerSample);
          return;
        }
        audioFormatRef.current = {
          channels: view.getUint16(22, true),
          sampleRate: view.getUint32(24, true),
          bitsPerSample,
        };
        ensurePlaybackContext();
        if (bytes.length > 44) {
          const pcm = bytes.slice(44);
          turnPcmRef.current.push(pcm);
          enqueuePcm(pcm);
        }
        return;
      }

      turnPcmRef.current.push(bytes);
      enqueuePcm(bytes);
    },
    [enqueuePcm, ensurePlaybackContext],
  );

  /**
   * 音声や商品リンクを、そのターンの回答の吹き出しに付ける。
   *
   * 回答テキストは少しずつ描画されるので、吹き出しがまだ作られていないことがある。
   * その場合は預かっておき、吹き出しを作るときに一緒に付ける。
   */
  const attachToAnswer = useCallback(
    (extras: { audioUrl?: string; products?: GeminiProduct[] }) => {
      if (!answerAppendedRef.current) {
        turnExtrasRef.current = { ...turnExtrasRef.current, ...extras };
        return;
      }

      setConversation((prev) => {
        // findLastIndex は新しめのブラウザにしか無い。ウィジェットは顧客サイトで動くので使わない
        let index = -1;
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          if (prev[i].role === "assistant") {
            index = i;
            break;
          }
        }
        if (index < 0) return prev;

        const next = [...prev];
        next[index] = { ...next[index], ...extras };
        return next;
      });
    },
    [],
  );

  /**
   * 控えたPCMをWAVにして、直前の回答から聞き直せるようにする。
   *
   * 受け取ったバイト列の先頭にもWAVヘッダは付いているが、長さが未確定の
   * ストリーム用なので作り直す。長さが入っていないと再生位置を出せない。
   */
  const attachTurnAudio = useCallback(() => {
    const chunks = turnPcmRef.current;
    turnPcmRef.current = [];

    const format = audioFormatRef.current;
    if (!format || chunks.length === 0) return;

    const url = URL.createObjectURL(encodeWavBlob(chunks, format));

    // 古い控えから順に解放する。会話が長くなるほどメモリを食うため
    audioUrlsRef.current.push(url);
    while (audioUrlsRef.current.length > AUDIO_HISTORY_LIMIT) {
      const expired = audioUrlsRef.current.shift();
      if (expired) URL.revokeObjectURL(expired);
    }

    attachToAnswer({ audioUrl: url });
  }, [attachToAnswer]);

  /** ターンぶんを鳴らし切り、次のターンのヘッダを読める状態に戻す */
  const flushTurnAudio = useCallback(() => {
    scheduleQueuedPcm();
    attachTurnAudio();
    pcmQueueRef.current = [];
    pcmQueueLengthRef.current = 0;
    audioFormatRef.current = null;
    playbackStartedRef.current = false;
    nextPlayTimeRef.current += TURN_GAP_SEC;
  }, [attachTurnAudio, scheduleQueuedPcm]);

  /** 割り込み時は予約済みの音まで止める。残すと新しい回答と重なって聞こえる */
  const stopPlayback = useCallback(() => {
    const context = playContextRef.current;
    const now = context?.currentTime ?? 0;
    for (const { source, gain } of scheduledSourcesRef.current) {
      source.onended = null;
      try {
        if (context) {
          // 鳴っている途中でいきなり止めると、切り口でプチッと鳴る
          gain.gain.cancelScheduledValues(now);
          gain.gain.setValueAtTime(gain.gain.value, now);
          gain.gain.linearRampToValueAtTime(0, now + FADE_SEC);
          source.stop(now + FADE_SEC);
        } else {
          source.stop();
        }
      } catch {
        // 再生前や再生済みの stop は無視してよい
      }
    }
    scheduledSourcesRef.current = [];
    pcmQueueRef.current = [];
    pcmQueueLengthRef.current = 0;
    audioFormatRef.current = null;
    audioPendingRef.current = false;
    playbackStartedRef.current = false;
    nextPlayTimeRef.current = now;
    setIsSpeaking(false);
  }, []);

  const stopReveal = useCallback(() => {
    if (revealTimerRef.current !== null) {
      window.clearInterval(revealTimerRef.current);
      revealTimerRef.current = null;
    }
  }, []);

  /** 進行中の回答を確定して会話ログに積む。未表示の残りがあっても打ち切る */
  const flushPendingAnswer = useCallback(() => {
    stopReveal();
    const text = accumulatedAnswerRef.current;
    accumulatedAnswerRef.current = "";
    revealedLengthRef.current = 0;
    pendingDoneRef.current = false;
    if (!text) return;
    setCurrentAnswer("");

    const extras = turnExtrasRef.current;
    turnExtrasRef.current = {};
    answerAppendedRef.current = true;

    setConversation((prev) => [
      ...prev,
      { role: "assistant", text, timestamp: new Date().toISOString(), ...extras },
    ]);
  }, [stopReveal]);

  /** 届いたチャンクを少しずつ画面に出す */
  const startReveal = useCallback(() => {
    if (revealTimerRef.current !== null) return;
    revealTimerRef.current = window.setInterval(() => {
      const full = accumulatedAnswerRef.current;
      const remaining = full.length - revealedLengthRef.current;

      if (remaining > 0) {
        revealedLengthRef.current += Math.max(1, Math.ceil(remaining / REVEAL_DIVISOR));
        setCurrentAnswer(full.slice(0, revealedLengthRef.current));
        return;
      }
      // 全文を出し切った。ターンが終わっていれば会話ログへ移す
      if (pendingDoneRef.current) {
        flushPendingAnswer();
      }
    }, REVEAL_TICK_MS);
  }, [flushPendingAnswer]);

  // ---------------------------------------------------------------------------
  // 接続（セッション作成 → WebSocket）
  // ---------------------------------------------------------------------------

  /**
   * セッションを作ってWebSocketを開く。
   * withMic=false ならマイクを要求せず、テキストだけで会話できる状態にする
   * （テキスト入力が主のページでは、開いた瞬間に許可を求めない方がよい）
   */
  const connect = useCallback(async (options?: { withMic?: boolean }) => {
    if (wsRef.current) return;
    const withMic = options?.withMic ?? true;

    // 自動再生の制限を避けるため、ユーザー操作から呼ばれるここで再生側を用意する
    ensurePlaybackContext();

    setStatus("connecting");
    setConversation([]);
    setCurrentTranscript("");
    setCurrentAnswer("");
    setWarning("");
    setMetrics(null);
    accumulatedAnswerRef.current = "";

    // マイク許可はユーザー操作のコンテキスト内で取得する必要がある
    if (withMic) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: TARGET_SAMPLE_RATE,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        mediaStreamRef.current = stream;
        pendingAutoRecordRef.current = true;
      } catch (error) {
        // マイクを断られてもテキストでは会話できるので、接続自体は続ける
        console.error("Failed to get microphone:", error);
        setWarning("マイクを使用できません。テキストで質問できます。");
      }
    }

    let sessionId: string;
    try {
      const session = await createPublicGeminiChatSession(agentId);
      sessionId = session.id;
    } catch (error) {
      console.error("Failed to create session:", error);
      setStatus("error");
      return;
    }

    const wsUrl = `${getApiBaseURL().replace(/^http/, "ws")}/api/v1/public/gemini-chatbots/${agentId}/ws?sessionId=${sessionId}`;
    const ws = new WebSocket(wsUrl);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onmessage = (event) => {
      // 合成音声はバイナリで届く。1ターンぶんの先頭にWAVヘッダが入っている
      if (event.data instanceof ArrayBuffer) {
        collectAudioChunk(event.data);
        return;
      }
      if (typeof event.data !== "string") return;

      try {
        const message = JSON.parse(event.data);

        switch (message.type) {
          case "status":
            if (message.message === "ready") {
              setStatus("ready");
              if (pendingAutoRecordRef.current) {
                activateRecording();
              }
            }
            break;

          case "warning":
            setWarning(message.message ?? "");
            break;

          case "speech_started":
            // 聞き取りが届く前に「話しているのを認識している」ことを見せる
            setIsSpeechPending(true);
            break;

          case "speech_cancelled":
            // 物音として捨てられたターン。待機表示を消す
            setIsSpeechPending(false);
            break;

          case "transcript":
            // 文字起こしが最初に届く。ここを回答待ちの起点として計測する
            if (turnStartedAtRef.current === null) {
              turnStartedAtRef.current = performance.now();
              // 前の回答がまだ鳴っていれば止める。新しい回答と重ねない
              stopPlayback();
            }
            setIsSpeechPending(false);
            setCurrentTranscript(message.text ?? "");
            break;

          case "user_text":
            setIsSpeechPending(false);
            setCurrentTranscript("");
            setConversation((prev) => [
              ...prev,
              { role: "user", text: message.text ?? "", timestamp: new Date().toISOString() },
            ]);
            break;

          case "llm_text_chunk":
            if (!accumulatedAnswerRef.current) {
              // 新しいターンの始まり。前のターンの預かり分を持ち越さない
              turnExtrasRef.current = {};
              answerAppendedRef.current = false;
              if (turnStartedAtRef.current !== null) {
                responseStartMsRef.current = Math.round(
                  performance.now() - turnStartedAtRef.current,
                );
              }
            }
            accumulatedAnswerRef.current += message.text ?? "";
            startReveal();
            break;

          case "audio_start":
            audioPendingRef.current = true;
            ensurePlaybackContext();
            setIsSpeaking(true);
            break;

          case "audio_complete":
            // 1ターンぶんが届き切った。残りを鳴らし切って表示を戻す
            audioPendingRef.current = false;
            flushTurnAudio();
            if (scheduledSourcesRef.current.length === 0) {
              setIsSpeaking(false);
            }
            break;

          case "metrics":
            setMetrics({
              responseStartMs: responseStartMsRef.current,
              ttftMs: message.ttftMs ?? 0,
              totalMs: message.totalMs ?? 0,
              promptTokens: message.promptTokens ?? 0,
              outputTokens: message.outputTokens ?? 0,
              cachedTokens: message.cachedTokens ?? 0,
            });
            break;

          case "response_done":
            pendingDoneRef.current = true;
            // 表示が残っていればタイマー側で確定する。チャンクが無いターンはここで終わり
            if (!accumulatedAnswerRef.current) {
              flushPendingAnswer();
            }
            turnStartedAtRef.current = null;
            break;

          case "products":
            // 回答に出てきた登録商品。読み上げには乗らず、画面にだけ出す
            attachToAnswer({ products: message.products ?? [] });
            break;

          case "interrupted":
            // 割り込みが入ったターンの途中結果は捨てる
            stopPlayback();
            stopReveal();
            // 途中まで届いた音や商品リンクを前の回答に付けない
            turnPcmRef.current = [];
            turnExtrasRef.current = {};
            accumulatedAnswerRef.current = "";
            revealedLengthRef.current = 0;
            pendingDoneRef.current = false;
            setIsSpeechPending(false);
            setCurrentAnswer("");
            setCurrentTranscript("");
            turnStartedAtRef.current = null;
            break;

          case "error":
            console.error("Gemini chatbot error:", message.message);
            setWarning(message.message ?? "");
            break;
        }
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
      }
    };

    ws.onerror = () => {
      setStatus("error");
    };

    ws.onclose = () => {
      stopPlayback();
      flushPendingAnswer();
      wsRef.current = null;
      setStatus("disconnected");
      setIsRecording(false);
    };
  }, [
    agentId,
    activateRecording,
    flushPendingAnswer,
    startReveal,
    stopReveal,
    collectAudioChunk,
    flushTurnAudio,
    stopPlayback,
    ensurePlaybackContext,
  ]);

  // ---------------------------------------------------------------------------
  // 切断
  // ---------------------------------------------------------------------------

  const disconnect = useCallback(() => {
    flushPendingAnswer();
    stopPlayback();

    if (playContextRef.current) {
      playContextRef.current.close();
      playContextRef.current = null;
    }

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    pendingAutoRecordRef.current = false;

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000);
      wsRef.current = null;
    }

    setStatus("disconnected");
    setIsRecording(false);
    setCurrentTranscript("");
    setCurrentAnswer("");
    turnStartedAtRef.current = null;
  }, [flushPendingAnswer, stopPlayback]);

  const stopRecording = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "stop_recording" }));
    }
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setIsRecording(false);
  }, []);

  const startRecording = useCallback(async () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    if (isRecording) return;

    if (!mediaStreamRef.current) {
      try {
        mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: TARGET_SAMPLE_RATE,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
      } catch (error) {
        console.error("Failed to get microphone:", error);
        return;
      }
    }
    activateRecording();
  }, [isRecording, activateRecording]);

  const sendTextMessage = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    flushPendingAnswer();
    // 前の回答を読み上げ中なら止める。ユーザーは次の質問に移っている
    stopPlayback();
    ensurePlaybackContext();
    turnStartedAtRef.current = performance.now();

    wsRef.current.send(JSON.stringify({ type: "text", text: trimmed }));
    setConversation((prev) => [
      ...prev,
      { role: "user", text: trimmed, timestamp: new Date().toISOString() },
    ]);
  }, [flushPendingAnswer, stopPlayback, ensurePlaybackContext]);

  useEffect(() => {
    return () => {
      processorRef.current?.disconnect();
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      audioContextRef.current?.close();
      playContextRef.current?.close();
      wsRef.current?.close();
      // 控えたBlobは明示的に解放する。放っておくとページを閉じるまで残る
      audioUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      audioUrlsRef.current = [];
    };
  }, []);

  return {
    status,
    isRecording,
    conversation,
    currentTranscript,
    isSpeechPending,
    currentAnswer,
    isSpeaking,
    warning,
    metrics,
    connect,
    disconnect,
    startRecording,
    stopRecording,
    sendTextMessage,
  };
};

export default useGeminiWs;
